import type { ColumnsType } from 'antd/lib/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import { useGetTopSalesPersonsQuery } from '../../../../api/queries/getTopSalesPersons';
import { type StatisticFilterPayload, ModuleRole, ApplicationStage } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import { useMultipleDealerIds } from '../../../../components/contexts/DealerContextManager';
import { type TopSalesManData } from './useTopSalesManWithApplicationData';

export interface TopSalesManWithLeadData {
    key: string;
    name: string;
    alias: string;
    noOfLeads: number;
}

const columns = (t: TFunction): ColumnsType<TopSalesManWithLeadData> => [
    {
        ...t('dashboard:topSalesManWithLead.tableColumns.name', { returnObjects: true }),
        dataIndex: 'name',
        key: 'name',
        fixed: true,
    },
    {
        ...t('dashboard:topSalesManWithLead.tableColumns.alias', { returnObjects: true }),
        dataIndex: 'alias',
        key: 'alias',
    },
    {
        ...t('dashboard:topSalesManWithLead.tableColumns.noOfLeads', { returnObjects: true }),
        dataIndex: 'noOfLeads',
        key: 'noOfLeads',
    },
];

const useTopSalesManWithLeadData = () => {
    const { t } = useTranslation(['dashboard']);
    const cols = useMemo(() => columns(t), [t]);
    const [isLoading, setIsLoading] = useState(false);

    // prepare filter
    const company = useCompany(true);
    const { dealerIds } = useMultipleDealerIds();
    const filter: StatisticFilterPayload = useMemo(
        () => ({
            companyId: company?.id,
            dealerIds,
            moduleRole: ModuleRole.Application,
            stage: ApplicationStage.Lead,
        }),
        [company, dealerIds]
    );

    // fetch data
    const { data, loading, error } = useGetTopSalesPersonsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter,
            sortParam: 'count',
        },
        skip: !company?.id,
    });

    useEffect(() => {
        setIsLoading(loading);
    }, [loading]);

    // transform response data
    const generateData = useCallback(
        async (applyData: (data: TopSalesManData<TopSalesManWithLeadData>) => void) => {
            if (!data?.metrics) {
                applyData(null);

                return;
            }

            setIsLoading(true);

            const currentMonth: TopSalesManWithLeadData[] =
                data.metrics.currentMonth?.map(({ name, alias, count }) => ({
                    key: name,
                    name,
                    alias,
                    noOfLeads: count,
                })) || [];
            const lastMonth: TopSalesManWithLeadData[] =
                data.metrics.lastMonth?.map(({ name, alias, count }) => ({
                    key: name,
                    name,
                    alias,
                    noOfLeads: count,
                })) || [];

            applyData({ columns: cols, data: { currentMonth, lastMonth } });
            setIsLoading(false);
        },
        [data, cols]
    );

    return useMemo(
        () => ({
            isLoading,
            error,
            generateData,
        }),
        [isLoading, error, generateData]
    );
};

export default useTopSalesManWithLeadData;
